import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import PannelBody from "../../../fragments/PannelBody"
import Button from "../../../common/Button"
import { patientRelationsOptions, practicesLocationsGroupedOptions, resolvePatientRelationValue } from "../../../../lib/systemData"
import DatepickerWithLabel from "../../../common/interactive/DatepickerWithLabel"
import MultipleCheckboxCell from "../../../common/MultipleCheckboxCell"
import RequestRide from "./RequestRide"
import FalseInputNumber from "../../../common/FalseInputNumber"
import { addTransportationRide, deleteTransportationRide, getTransportationAvailableAppointments } from "../../../../lib/fetchApi"
import { addZero, formatDate, formatDateLocal, getLatitudeLongitude, isPersonAMinor } from "../../../../lib/script"
import { addressesLookTheSame } from "../../../../lib/rideAddressMatch"
import { formatPatientName } from "../../../../lib/patientName"
import { miscellaneous } from "../../../../lib/misc"
import { notificationActions } from "../../../../store/notifications"
import { isAppointmentRescheduledError } from "../../../../lib/transportation"
import { caseActions } from "../../../../store/case"
import CustomSelectGrouped from "../../../common/interactive/CustomSelectGrouped"
import TimePickerWithLabel from "../../../common/TimePickerWithLabel"
import InputBox from "../../../common/interactive/InputBox"
import CustomSelect from "../../../common/interactive/CustomSelect"
import FormTextareaBox from "../../../common/interactive/FormTextareaBox"

import classes from '../forms/Create.module.css'
import OutsideClickComponent from "../../../fragments/OutsideClickComponent"

const activePracticesLocationsGroupedOptions = () => {
    const groupedOptions = practicesLocationsGroupedOptions()
    if (!Array.isArray(groupedOptions)) {
        return groupedOptions
    }

    return groupedOptions
        .filter((practiceGroup) => !practiceGroup?.isDisabled)
        .map((practiceGroup) => ({
            ...practiceGroup,
            options: Array.isArray(practiceGroup?.options)
                ? practiceGroup.options.filter((location) => !location?.isDisabled)
                : []
        }))
        .filter((practiceGroup) => practiceGroup.options.length > 0)
}

const formatContactAddress = (ci) => {
    if (!ci?.address_1 && !ci?.city && !ci?.state && !ci?.zip) {
        return null
    }
    return `${ci.address_1}, ${ci.city}, ${ci.state}, ${ci.zip}`
}

const getClinicAddressByLocationId = (groupedLocations, locationId) => {
    if (!locationId || !Array.isArray(groupedLocations)) {
        return null
    }

    for (const practiceGroup of groupedLocations) {
        const matchedLocation = practiceGroup?.options?.find(
            (option) => Number(option?.value) === Number(locationId)
        )
        if (matchedLocation) {
            return formatContactAddress(matchedLocation.contact_information)
        }
    }

    return null
}

const HOME_ADDRESS_REQUIRED_MESSAGE = "Please enter a valid address."
const HOME_ADDRESS_GEOCODE_ERROR_MESSAGE = "We could not find that address. Please check and try again."
const HOME_ADDRESS_ZIP_REQUIRED_MESSAGE = "Please enter a full address that includes a ZIP code."
const HOME_ADDRESS_PARTIAL_MATCH_MESSAGE = "Please enter a more precise, correct address."
const CLINIC_ADDRESS_REQUIRED_MESSAGE = "Clinic address is required for this trip."
const ADDRESS_OVERRIDE_REASON_MIN = 20
const ADDRESS_OVERRIDE_REASON_REQUIRED_MESSAGE = `Please enter a reason of at least ${ADDRESS_OVERRIDE_REASON_MIN} characters.`

const addressHasUsZip = (address) => /\b\d{5}(?:-\d{4})?\b/.test(`${address || ""}`)

const resolveTripEndpoints = (pickupAtHome, homeAddress, clinicAddress) => {
    if (pickupAtHome) {
        return {
            pickup_at: "home",
            originAddress: homeAddress,
            destinationAddress: clinicAddress,
        }
    }
    return {
        pickup_at: "clinic",
        originAddress: clinicAddress,
        destinationAddress: homeAddress,
    }
}

const getTripEndpointValidationError = (endpoints) => {
    if (!endpoints.originAddress?.trim()) {
        return endpoints.pickup_at === "clinic"
            ? { message: CLINIC_ADDRESS_REQUIRED_MESSAGE, field: "clinic" }
            : { message: HOME_ADDRESS_REQUIRED_MESSAGE, field: "home" }
    }
    if (!endpoints.destinationAddress?.trim()) {
        return endpoints.pickup_at === "home"
            ? { message: CLINIC_ADDRESS_REQUIRED_MESSAGE, field: "clinic" }
            : { message: HOME_ADDRESS_REQUIRED_MESSAGE, field: "home" }
    }
    return null
}

const AddNewTrip = ({closeModal, edit, disableOverflow}) => {
    const formRef = useRef(null)

    const isForced = useRef(false)
    const isForcedDistance = useRef(false)
    const pendingConfirmationType = useRef(null)
    const skipEditAppointmentRestore = useRef(false)
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const roundUpToNext10Minutes = useCallback((date) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return null
        }
        const rounded = new Date(date.getTime())
        rounded.setSeconds(0, 0)
        const mins = rounded.getMinutes()
        const roundedMins = Math.ceil(mins / 10) * 10
        if (roundedMins === 60) {
            rounded.setHours(rounded.getHours() + 1, 0, 0, 0)
        } else {
            rounded.setMinutes(roundedMins, 0, 0)
        }
        return rounded
    }, [])

    const now = new Date()
    const nowPlus10mins = roundUpToNext10Minutes(new Date(now.getTime() + 10 * 60 * 1000))
    const fallbackDate = formatDate(nowPlus10mins || now)
    const fallbackTime = `${addZero((nowPlus10mins || now).getHours())}:${addZero((nowPlus10mins || now).getMinutes())}`
    
    const activeCase = useSelector(state => state.case.activeCase)
    const [futureAppointments, setFutureAppointments] = useState([])
    const [appointmentsLoading, setAppointmentsLoading] = useState(false)
    const caseId = activeCase?.case?.id
    const parseAppointmentDateTime = useCallback((appointment) => {
        if (!appointment?.date || !appointment?.begin_time) {
            return null
        }

        const datePart = String(appointment.date).trim().split(' ')[0]
        const timeRaw = String(appointment.begin_time).trim()
        const timeParts = timeRaw.split(':')
        if (timeParts.length < 2) {
            return null
        }

        const hours = Number(timeParts[0])
        const minutes = Number(timeParts[1])
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return null
        }

        const parsed = new Date(`${datePart}T${addZero(hours)}:${addZero(minutes)}:00`)
        if (Number.isNaN(parsed.getTime())) {
            return null
        }

        return parsed
    }, [])

    const getAppointmentLocationId = useCallback((appointment) => {
        if (!appointment) {
            return null
        }
        const directCandidates = [
            appointment.location_id,
            appointment.practice_location_id,
            appointment.case_practice_location_id,
            appointment?.location?.id,
            appointment?.practice_location?.id,
            appointment?.practice?.location_id,
            appointment?.practice?.default_location_id,
        ]
        const pickedDirect = directCandidates.find((item) => item != null && item !== "")
        if (pickedDirect != null) {
            return Number(pickedDirect)
        }

        // Fallback: resolve location from case practice mapping when appointment only carries practice identifiers.
        const appointmentPracticeId =
            appointment.practice_id ||
            appointment?.practice?.id ||
            appointment.case_practice_id ||
            appointment?.case_practice?.id
        if (!appointmentPracticeId || !Array.isArray(activeCase?.practices)) {
            return null
        }

        const matchedCasePractice = activeCase.practices.find((item) => (
            Number(item?.id) === Number(appointmentPracticeId) ||
            Number(item?.practice_id) === Number(appointmentPracticeId)
        ))
        if (matchedCasePractice?.location_id != null && matchedCasePractice?.location_id !== "") {
            return Number(matchedCasePractice.location_id)
        }

        return null
    }, [activeCase?.practices])

    const getDefaultPickupFromAppointment = useCallback((appointment) => {
        const appointmentDateTime = parseAppointmentDateTime(appointment)
        if (!appointmentDateTime) {
            return null
        }

        const oneHourBeforeAppointment = new Date(appointmentDateTime.getTime() - 1 * 60 * 60 * 1000)
        const minPickupDateTime = roundUpToNext10Minutes(new Date(Date.now() + 10 * 60 * 1000))
        const pickupDateTimeRaw = oneHourBeforeAppointment > minPickupDateTime ? oneHourBeforeAppointment : minPickupDateTime
        const pickupDateTime = roundUpToNext10Minutes(pickupDateTimeRaw)

        if (Number.isNaN(pickupDateTime.getTime())) {
            return null
        }

        return {
            date: formatDate(pickupDateTime),
            time: `${addZero(pickupDateTime.getHours())}:${addZero(pickupDateTime.getMinutes())}`
        }
    }, [parseAppointmentDateTime, roundUpToNext10Minutes])

    const getDefaultReturnPickupFromAppointment = useCallback((appointment) => {
        const parseTimeOnDate = (timeValue) => {
            if (!appointment?.date || !timeValue) {
                return null
            }

            const datePart = String(appointment.date).trim().split(' ')[0]
            const timeRaw = String(timeValue).trim()
            const timeParts = timeRaw.split(':')
            if (timeParts.length < 2) {
                return null
            }

            const hours = Number(timeParts[0])
            const minutes = Number(timeParts[1])
            if (Number.isNaN(hours) || Number.isNaN(minutes)) {
                return null
            }

            const parsed = new Date(`${datePart}T${addZero(hours)}:${addZero(minutes)}:00`)
            if (Number.isNaN(parsed.getTime())) {
                return null
            }

            return parsed
        }

        const returnDateTime = parseTimeOnDate(appointment?.end_time) ?? parseTimeOnDate(appointment?.begin_time)
        if (!returnDateTime) {
            return null
        }

        const minPickupDateTime = roundUpToNext10Minutes(new Date(Date.now() + 10 * 60 * 1000))
        const pickupDateTime = returnDateTime <= minPickupDateTime
            ? minPickupDateTime
            : roundUpToNext10Minutes(returnDateTime)

        if (!pickupDateTime || Number.isNaN(pickupDateTime.getTime())) {
            return null
        }

        return {
            date: formatDate(pickupDateTime),
            time: `${addZero(pickupDateTime.getHours())}:${addZero(pickupDateTime.getMinutes())}`
        }
    }, [roundUpToNext10Minutes])

    const formatDateForAppointmentOption = useCallback((value) => {
        if (!value) {
            return "No date"
        }

        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return `${addZero(value.getMonth() + 1)}/${addZero(value.getDate())}/${value.getFullYear()}`
        }

        if (typeof value === "string") {
            const normalized = value.trim().split(" ")[0]
            if (normalized) {
                return formatDateLocal(normalized)
            }
        }

        return "No date"
    }, [])

    const [loading, setLoading] = useState(false)
    const [fullLocations] = useState(activePracticesLocationsGroupedOptions);
    const getPracticeAndLocationFromSettings = useCallback((appointment) => {
        const locationId = getAppointmentLocationId(appointment)
        if (!locationId || !Array.isArray(fullLocations)) {
            return {
                practiceName: "N/A",
                locationCode: "N/A",
            }
        }

        for (const practiceGroup of fullLocations) {
            const matchedLocation = practiceGroup?.options?.find((option) => Number(option?.value) === Number(locationId))
            if (matchedLocation) {
                return {
                    practiceName: practiceGroup?.label || "N/A",
                    locationCode:
                        matchedLocation?.location_code ||
                        matchedLocation?.code ||
                        matchedLocation?.short_code ||
                        "N/A",
                }
            }
        }

        return {
            practiceName: "N/A",
            locationCode: "N/A",
        }
    }, [fullLocations, getAppointmentLocationId])

    const patientHomeAddress = useMemo(() => {
        const ci = activeCase?.patient?.contact_information
        if (!ci?.address_1 && !ci?.city && !ci?.state && !ci?.zip) {
            return null
        }
        return `${ci.address_1}, ${ci.city}, ${ci.state}, ${ci.zip}`
    }, [activeCase?.patient?.contact_information])
    const [isAddressOverridden, setIsAddressOverridden] = useState(false)
    const [tripHomeAddress, setTripHomeAddress] = useState(edit?.address ?? patientHomeAddress ?? "")
    const isDifferentHomeAddress = isAddressOverridden && !addressesLookTheSame(tripHomeAddress, patientHomeAddress)
    const [tripHomeAddressError, setTripHomeAddressError] = useState(null)
    const [addressOverrideReason, setAddressOverrideReason] = useState("")
    const [addressReasonError, setAddressReasonError] = useState(null)
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(edit?.appointment_id ? Number(edit.appointment_id) : null)
    const [selectedClinic, setSelectedClinic] = useState(edit ? edit.location_id : null)
    const [selectedoptionForClinic, setSelectedoptionForClinic] = useState(null)
    const clinicAddress = useMemo(() => (
        formatContactAddress(selectedoptionForClinic?.contact_information)
        ?? getClinicAddressByLocationId(fullLocations, selectedClinic)
    ), [fullLocations, selectedClinic, selectedoptionForClinic])
    const [selectedDate, setSelectedDate] = useState(edit ? edit.date : fallbackDate)
    const [selectedTime, setSelectedTime] = useState(edit ? edit.time : fallbackTime)
    const [shareRide, setShareRide] = useState(edit ? edit.related_patients : null)
    const [guardianName, setGuardianName] = useState(edit?.guardian_name ?? activeCase?.patient?.guardian_name ?? "")
    const [guardianRelationship, setGuardianRelationship] = useState(resolvePatientRelationValue(edit?.guardian_relationship ?? activeCase?.patient?.guardian_relationship ?? null))

    const [rideData, setRideData] = useState(null)
    const [lyftResponse, setLyftResponse] = useState(null)

    const [nextStep, setNextStep] = useState(false)

    const [pickupat , setPickupat] = useState(true)
    const [relatedPatients, setRelatedPatients] = useState(null)

    const [showConfirmation, setShowConfirmation] = useState(null)
    const [appointmentRideConflict, setAppointmentRideConflict] = useState(null)
    const [pendingSubmitData, setPendingSubmitData] = useState(null)
    const [conflictActionLoading, setConflictActionLoading] = useState(false)
    const [showCancelRideConfirmation, setShowCancelRideConfirmation] = useState(false)
    const [suppressedRideAppointmentIds, setSuppressedRideAppointmentIds] = useState([])
    const isMinorPatient = activeCase?.patient?.dob ? isPersonAMinor(activeCase.patient.dob) : false
    const patientRelationshipOptionsData = useMemo(() => patientRelationsOptions(), [])
    const isAppointmentSelected = !!selectedAppointmentId
    const activeRides = useMemo(() => {
        if (!Array.isArray(activeCase?.case_transportation?.rides)) {
            return []
        }
        return activeCase.case_transportation.rides.filter((ride) => ride?.active == 1 && ride?.completed != 1)
    }, [activeCase?.case_transportation?.rides])
    const getRideAppointmentId = useCallback((ride) => {
        if (!ride) {
            return null
        }
        return (
            ride.appointment_id ??
            ride.case_appointment_id ??
            ride?.appointment?.id ??
            ride?.case_appointment?.id ??
            null
        )
    }, [])
    const getRideIdForDelete = useCallback((ride) => {
        if (!ride) {
            return null
        }
        return (
            ride?.id ??
            ride?.ride_id ??
            ride?.case_transportation_ride_id ??
            ride?.transportation_ride_id ??
            ride?.lyft_ride_id ??
            null
        )
    }, [])
    const hasActiveRideForAppointment = useCallback((appointmentId) => {
        if (!appointmentId) {
            return false
        }
        if (suppressedRideAppointmentIds.some((id) => Number(id) === Number(appointmentId))) {
            return false
        }
        return activeRides.some((ride) => Number(getRideAppointmentId(ride)) === Number(appointmentId))
    }, [activeRides, getRideAppointmentId, suppressedRideAppointmentIds])
    const getAppointmentActiveRide = useCallback((appointment) => {
        if (!appointment) {
            return null
        }
        if (suppressedRideAppointmentIds.some((id) => Number(id) === Number(appointment?.id))) {
            return null
        }

        // Source of truth from API: futureAppointment.ride
        const embeddedRide = appointment?.ride || null

        if (embeddedRide && embeddedRide?.active == 1 && embeddedRide?.completed != 1) {
            return embeddedRide
        }

        const appointmentId = appointment?.id
        if (appointmentId && hasActiveRideForAppointment(appointmentId)) {
            return activeRides.find((ride) => Number(getRideAppointmentId(ride)) === Number(appointmentId)) || null
        }

        return null
    }, [activeRides, getRideAppointmentId, hasActiveRideForAppointment, suppressedRideAppointmentIds])

    const appointmentSelectOptions = useMemo(() => {
        return futureAppointments
            .filter(Boolean)
            .map((appointment) => {
                const appointmentDateTime = parseAppointmentDateTime(appointment)
                let datePart = formatDateForAppointmentOption(appointment?.date)
                let timePart = appointment?.begin_time
                    ? miscellaneous.convert24to12(String(appointment.begin_time)) || appointment.begin_time
                    : "No time"
                if (appointmentDateTime) {
                    datePart = formatDateForAppointmentOption(appointmentDateTime)
                    timePart = miscellaneous.convert24to12(
                        `${addZero(appointmentDateTime.getHours())}:${addZero(appointmentDateTime.getMinutes())}`
                    )
                }
                const locationFromSettings = getPracticeAndLocationFromSettings(appointment)
                const practiceNamePart = locationFromSettings.practiceName
                const locationCodePart = locationFromSettings.locationCode
                const activeRideForAppointment = getAppointmentActiveRide(appointment)
                const hasActiveRide = !!activeRideForAppointment
                const rideIndicatorText = hasActiveRide ? " | 🚗" : ""
                const rideIndicatorHtml = hasActiveRide ? ` <span style="color:#d32f2f;">🚗</span>` : ""
                const baseLabel = `${datePart} ${timePart} | ${practiceNamePart} | ${locationCodePart}`

                return {
                    value: appointment?.id,
                    label: `${baseLabel}${rideIndicatorText}`,
                    complexLabel: `${baseLabel}${rideIndicatorHtml}`,
                    appointment,
                    appointmentDateTime,
                    hasActiveRide,
                    activeRideForAppointment,
                }
            })
            .filter((item) => item.value != null)
            .filter((item) => item.appointmentDateTime)
            .sort((a, b) => a.appointmentDateTime - b.appointmentDateTime)
    }, [futureAppointments, parseAppointmentDateTime, formatDateForAppointmentOption, getPracticeAndLocationFromSettings, getAppointmentActiveRide])
    const selectedAppointmentOption = useMemo(() => (
        appointmentSelectOptions.find((item) => Number(item?.value) === Number(selectedAppointmentId)) || null
    ), [appointmentSelectOptions, selectedAppointmentId])
    const canCancelConflictingRide = getRideIdForDelete(appointmentRideConflict) != null

    const refetchAvailableAppointments = useCallback(async () => {
        if (!caseId || !userToken) {
            return
        }
        setAppointmentsLoading(true)
        const response = await getTransportationAvailableAppointments(userToken, caseId)
        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        } else {
            const list = Array.isArray(response)
                ? response
                : (Array.isArray(response?.data) ? response.data : [])
            setFutureAppointments(list)
        }
        setAppointmentsLoading(false)
    }, [caseId, userToken, dispatch])

    const resetForRescheduledAppointment = useCallback(() => {
        skipEditAppointmentRestore.current = true
        setNextStep(false)
        disableOverflow(true)
        setSelectedAppointmentId(null)
        setRideData(null)
        setLyftResponse(null)
        setAppointmentRideConflict(null)
        setPendingSubmitData(null)
        setShowCancelRideConfirmation(false)
        setLoading(false)
        refetchAvailableAppointments()
    }, [disableOverflow, refetchAvailableAppointments])

    const createRideFromData = useCallback(async (continueData) => {
        const homeAddress = isAddressOverridden ? tripHomeAddress : (patientHomeAddress ?? "")
        const endpoints = resolveTripEndpoints(pickupat, homeAddress, clinicAddress)
        const pickup_at = endpoints.pickup_at

        const continueDataWithLocation = {
            ...continueData,
            address: homeAddress,
            pickup_at,
            origin_address: endpoints.originAddress,
            destination_address: endpoints.destinationAddress,
        }
        const originAddress = continueDataWithLocation.origin_address
        const destinationAddress = continueDataWithLocation.destination_address

        const endpointError = getTripEndpointValidationError({
            pickup_at,
            originAddress,
            destinationAddress,
        })
        if (endpointError) {
            if (endpointError.field === "home") {
                setTripHomeAddressError(endpointError.message)
            } else {
                dispatch(notificationActions.setError({ errorMessage: endpointError.message }))
            }
            setLoading(false)
            return
        }

        const showResult = async (result) => {
            continueDataWithLocation.lat = result.geometry.location.lat()
            continueDataWithLocation.lng = result.geometry.location.lng()

            if (!caseId) {
                setLoading(false)
                return
            }
            const { origin_address, destination_address, ...apiBody } = continueDataWithLocation
            const liftData = await addTransportationRide(userToken, caseId, apiBody, dispatch)

            if (liftData && liftData.confirmation) {
                pendingConfirmationType.current = liftData.confirmation_type || 'duplicate_ride'
                setShowConfirmation(liftData.confirmation)
            } else if (liftData && liftData.errorMessage) {
                dispatch(notificationActions.setError(liftData))
                if (isAppointmentRescheduledError(liftData)) {
                    resetForRescheduledAppointment()
                }
            } else if (liftData && liftData.estimates && liftData.estimates.cost_estimates && liftData.estimates.cost_estimates.some(item => item.upfront_cost_cents)) {
                setNextStep(true)
                disableOverflow(false)
                setRideData(continueDataWithLocation)
                setLyftResponse(liftData)
            } else {
                dispatch(notificationActions.setError({errorMessage: 'No Lyft rides are currently available for this route.',}))
            }

            setLoading(false)
        }

        if (!addressHasUsZip(homeAddress)) {
            setTripHomeAddressError(HOME_ADDRESS_ZIP_REQUIRED_MESSAGE)
            setLoading(false)
            return
        }

        getLatitudeLongitude(showResult, homeAddress, (reason) => {
            if (reason === "partial_match") {
                setTripHomeAddressError(HOME_ADDRESS_PARTIAL_MATCH_MESSAGE)
            } else {
                setTripHomeAddressError(HOME_ADDRESS_GEOCODE_ERROR_MESSAGE)
            }
            setLoading(false)
        })
    }, [caseId, clinicAddress, disableOverflow, dispatch, isAddressOverridden, patientHomeAddress, pickupat, resetForRescheduledAppointment, tripHomeAddress, userToken])

    const buildContinueData = useCallback(() => {
        const homeAddress = isAddressOverridden ? tripHomeAddress : (patientHomeAddress ?? "")
        const endpoints = resolveTripEndpoints(pickupat, homeAddress, clinicAddress)
        const continueData = {
            address: homeAddress,
            location_id: selectedClinic,
            pickup_at: endpoints.pickup_at,
            origin_address: endpoints.originAddress,
            destination_address: endpoints.destinationAddress,
            date: formatDate(selectedDate),
            time: selectedTime,
            is_shared: shareRide?.length > 0 ? true : false,
            related_patients: shareRide,
            step: 1,
        }

        if (isDifferentHomeAddress && addressOverrideReason.trim().length >= ADDRESS_OVERRIDE_REASON_MIN) {
            continueData.address_override_reason = addressOverrideReason.trim()
        }

        if (selectedAppointmentId) {
            continueData.appointment_id = selectedAppointmentId
        }

        if (isMinorPatient) {
            continueData.guardian_name = guardianName
            continueData.guardian_relationship = guardianRelationship
        }

        if (isForced.current) {
            continueData.force = 1
        }

        if (isForcedDistance.current) {
            continueData.force_distance = 1
        }

        return continueData
    }, [addressOverrideReason, clinicAddress, guardianName, guardianRelationship, isAddressOverridden, isDifferentHomeAddress, isMinorPatient, patientHomeAddress, pickupat, selectedAppointmentId, selectedClinic, selectedDate, selectedTime, shareRide, tripHomeAddress])

    const chooseDifferentAppointmentHandler = () => {
        setSelectedAppointmentId(null)
        setAppointmentRideConflict(null)
        setPendingSubmitData(null)
        setConflictActionLoading(false)
        setShowCancelRideConfirmation(false)
        setLoading(false)
    }

    const createNewRideHandler = () => {
        const appointmentId = pendingSubmitData?.appointment_id ?? selectedAppointmentId
        if (!appointmentId) {
            chooseDifferentAppointmentHandler()
            return
        }

        const selectedAppointment = futureAppointments.find((item) => Number(item?.id) === Number(appointmentId))
        const associatedRide =
            appointmentRideConflict
            ?? (selectedAppointment ? getAppointmentActiveRide(selectedAppointment) : null)
            ?? activeRides.find((ride) => Number(getRideAppointmentId(ride)) === Number(appointmentId))

        setSuppressedRideAppointmentIds((prevState) => (
            prevState.some((id) => Number(id) === Number(appointmentId))
                ? prevState
                : [...prevState, appointmentId]
        ))
        setPickupat(false)

        if (selectedAppointment) {
            const returnDefaults = getDefaultReturnPickupFromAppointment(selectedAppointment)
            if (returnDefaults) {
                setSelectedDate(returnDefaults.date)
                setSelectedTime(returnDefaults.time)
            }
        }

        const rideLocationId = associatedRide?.location_id
        const appointmentLocationId = selectedAppointment ? getAppointmentLocationId(selectedAppointment) : null
        const clinicLocationId =
            (rideLocationId != null && rideLocationId !== "" ? Number(rideLocationId) : null)
            ?? (appointmentLocationId != null ? appointmentLocationId : null)

        if (clinicLocationId != null) {
            setSelectedClinic(clinicLocationId)
        }

        setAppointmentRideConflict(null)
        setPendingSubmitData(null)
        setConflictActionLoading(false)
        setShowCancelRideConfirmation(false)
        setLoading(false)
    }

    const confirmCancelRideHandler = async () => {
        setShowCancelRideConfirmation(false)
        await cancelRideAndProceedHandler()
    }

    const cancelRideAndProceedHandler = async () => {
        const rideIdToCancel = getRideIdForDelete(appointmentRideConflict)
        const continueData = pendingSubmitData || buildContinueData()
        if (!rideIdToCancel || !continueData?.appointment_id) {
            dispatch(notificationActions.setError({ errorMessage: "Unable to cancel existing ride. Missing ride reference." }))
            return
        }

        setConflictActionLoading(true)
        if (!caseId) {
            setConflictActionLoading(false)
            setLoading(false)
            return
        }
        const deleteRide = await deleteTransportationRide(userToken, caseId, rideIdToCancel, dispatch)
        if (deleteRide && deleteRide.errorMessage) {
            dispatch(notificationActions.setError(deleteRide))
            setConflictActionLoading(false)
            setLoading(false)
            return
        }

        if (continueData?.appointment_id) {
            setSuppressedRideAppointmentIds((prevState) => (
                prevState.some((id) => Number(id) === Number(continueData.appointment_id))
                    ? prevState
                    : [...prevState, continueData.appointment_id]
            ))
            setFutureAppointments((prevState) => (
                Array.isArray(prevState)
                    ? prevState.map((item) => (
                        Number(item?.id) === Number(continueData.appointment_id)
                            ? { ...item, ride: null }
                            : item
                    ))
                    : prevState
            ))
        }

        setAppointmentRideConflict(null)
        // Keep selected appointment and updated form state, but do not auto-fetch from Lyft.
        // User will explicitly click "Fetch From Lyft" when ready.
        setPendingSubmitData(null)
        setConflictActionLoading(false)
        setLoading(false)
    }

    async function formOnSubmitHandler(event) {
        event.preventDefault()
        setLoading(true)

        if (!selectedAppointmentId) {
            dispatch(notificationActions.setError({ errorMessage: "Please select an appointment before creating a trip." }))
            setLoading(false)
            return
        }

        if (!(selectedClinic && selectedDate && selectedTime)) {
            setNextStep(false)
            setLoading(false)
            return
        }

        const homeAddress = isAddressOverridden ? tripHomeAddress : (patientHomeAddress ?? "")
        const endpoints = resolveTripEndpoints(pickupat, homeAddress, clinicAddress)
        const endpointError = getTripEndpointValidationError(endpoints)
        if (endpointError) {
            if (endpointError.field === "home") {
                setTripHomeAddressError(endpointError.message)
            } else {
                dispatch(notificationActions.setError({ errorMessage: endpointError.message }))
            }
            setLoading(false)
            return
        }

        if (isDifferentHomeAddress && addressOverrideReason.trim().length < ADDRESS_OVERRIDE_REASON_MIN) {
            setAddressReasonError(ADDRESS_OVERRIDE_REASON_REQUIRED_MESSAGE)
            setLoading(false)
            return
        }

        setTripHomeAddressError(null)
        setAddressReasonError(null)

        const continueData = buildContinueData()
        if (pickupat) {
            const isConflictSuppressed = suppressedRideAppointmentIds.some(
                (id) => Number(id) === Number(selectedAppointmentId)
            )
            if (!isConflictSuppressed) {
                const conflictRide = activeRides.find((ride) => Number(getRideAppointmentId(ride)) === Number(selectedAppointmentId))
                if (conflictRide) {
                    setPendingSubmitData(continueData)
                    setAppointmentRideConflict(conflictRide)
                    return
                }
            }
        }

        await createRideFromData(continueData)
    }
    
    const preventImplicitEnterSubmit = (event) => {
        if (event.key !== "Enter") {
            return
        }
        const targetTag = event?.target?.tagName
        if (targetTag === "INPUT" || targetTag === "TEXTAREA") {
            event.preventDefault()
        }
    }


    const reverseOrderHandler = () => {
        if (!isAppointmentSelected) {
            return
        }
        setPickupat((prevState) => {
            const nextPickupat = !prevState
            if (!nextPickupat) {
                setAppointmentRideConflict(null)
                setPendingSubmitData(null)
            }
            return nextPickupat
        })
    }

    const setClinicHandler = (value) => {
        setSelectedClinic(value.data.value)
    }

    const setDateHandler = (value) => {
        setSelectedDate(value.data.value)
    }

    const setTimeHandler = (value) => {
        setSelectedTime(value.data.value)
    }

    const setRelatedPatientsArray = (arr) => {
        if (arr && arr.idArr) {
            let idArray = []
            arr.idArr.forEach(item => {
                if (item?.id != null) {
                    idArray.push(item.id)
                }
            })

            setShareRide(idArray)
        }
    }

    const setGuardianNameHandler = (value) => {
        setGuardianName(value?.data?.value ?? "")
    }

    const setTripHomeAddressHandler = (value) => {
        setTripHomeAddress(value?.data?.value ?? "")
        setTripHomeAddressError(null)
    }

    const startAddressOverride = () => {
        setIsAddressOverridden(true)
        setTripHomeAddress("")
        setAddressOverrideReason("")
        setTripHomeAddressError(null)
        setAddressReasonError(null)
    }

    const setAddressOverrideReasonHandler = (event) => {
        setAddressOverrideReason(event?.target?.value ?? "")
        setAddressReasonError(null)
    }

    const revertToPatientAddress = () => {
        setIsAddressOverridden(false)
        setTripHomeAddress(patientHomeAddress ?? "")
        setAddressOverrideReason("")
        setTripHomeAddressError(null)
        setAddressReasonError(null)
    }

    const setGuardianRelationshipHandler = (value) => {
        setGuardianRelationship(value?.data?.value ?? null)
    }

    const setAppointmentHandler = (value) => {
        const appointmentId = value?.data?.value ? Number(value.data.value) : null
        setSelectedAppointmentId(appointmentId)

        if (!pickupat) {
            return
        }

        const selectedOption = value?.option
        if (selectedOption?.hasActiveRide) {
            setPendingSubmitData(null)
            setAppointmentRideConflict(selectedOption.activeRideForAppointment || { id: null, appointment_id: appointmentId })
            return
        }

        if (appointmentId && hasActiveRideForAppointment(appointmentId)) {
            const conflictRide = activeRides.find((ride) => Number(getRideAppointmentId(ride)) === Number(appointmentId)) || { id: null, appointment_id: appointmentId }
            setPendingSubmitData(null)
            setAppointmentRideConflict(conflictRide)
        }
    }

    useEffect(() => {
        if (isAddressOverridden) {
            return
        }
        if (edit?.address) {
            return
        }
        if (patientHomeAddress) {
            setTripHomeAddress(patientHomeAddress)
        }
    }, [edit?.address, isAddressOverridden, patientHomeAddress])

    useEffect(() => {
        let clinic = selectedClinic || edit?.location_id
        if (Array.isArray(fullLocations) && clinic) {            
            fullLocations.forEach(item => {
                if (Array.isArray(item?.options)) {
                    item.options.forEach(elem => {
                        if (elem?.value == clinic) {
                            setSelectedoptionForClinic(elem)
                        }
                    })
                }
            })
        }
    }, [selectedClinic, edit?.location_id, fullLocations])


    useEffect(() => {
        if (activeCase) {
            const arr = [...(activeCase?.related_patients ?? [])]
                .filter((item) => item && item.id != null)
            setRelatedPatients(arr)
        }        
    }, [activeCase])

    useEffect(() => {
        refetchAvailableAppointments()
    }, [refetchAvailableAppointments])

    useEffect(() => {
        if (!selectedAppointmentId) {
            return
        }

        const selectedAppointment = futureAppointments.find((item) => Number(item?.id) === Number(selectedAppointmentId))
        if (!selectedAppointment) {
            return
        }

        const appointmentDefaults = getDefaultPickupFromAppointment(selectedAppointment)
        if (appointmentDefaults) {
            setSelectedDate(appointmentDefaults.date)
            setSelectedTime(appointmentDefaults.time)
        }

        const locationId = getAppointmentLocationId(selectedAppointment)
        if (locationId) {
            setSelectedClinic(locationId)
        }
    }, [selectedAppointmentId, futureAppointments, getDefaultPickupFromAppointment, getAppointmentLocationId])

    useEffect(() => {
        if (selectedAppointmentId || skipEditAppointmentRestore.current) {
            return
        }
        const selectedAppointmentFromEdit = edit?.appointment_id ? Number(edit.appointment_id) : null
        if (selectedAppointmentFromEdit) {
            setSelectedAppointmentId(selectedAppointmentFromEdit)
        }
    }, [edit?.appointment_id, selectedAppointmentId])

    function goBackHandler(value) {
        setNextStep(value)
        disableOverflow(true)
    }

    function closeConfirmationHandler() {
        setShowConfirmation(null)
        pendingConfirmationType.current = null
    }

    function confirmHandler() {
        setShowConfirmation(null)

        if (pendingConfirmationType.current === 'distance') {
            isForcedDistance.current = true
        } else {
            isForced.current = true
        }
        pendingConfirmationType.current = null

        if (formRef && formRef.current) {
            formRef.current.dispatchEvent(
                new Event("submit", { bubbles: true, cancelable: true, force: true })
            )
        }
    }

    return <>
        {!nextStep && <form onSubmit={formOnSubmitHandler} onKeyDown={preventImplicitEnterSubmit} ref={formRef}>
            <PannelBody padding='large' background={'var(--line-transparent)'}>
                <div className={classes.subtitle}>
                    Patient
                </div>
                {activeCase && activeCase.patient && <>
                    {formatPatientName(activeCase.patient, { order: 'lastFirst' })}
                </>}
            </PannelBody>
            <PannelBody>
                <div className={classes.inner_title} style={{ marginTop: "10px" }}>
                    <h3>Appointment</h3>
                </div>
                <CustomSelect
                    label={'Select Appointment'}
                    name={'appointment_id'}
                    optionsData={appointmentSelectOptions}
                    preSelectedValue={selectedAppointmentId}
                    action={setAppointmentHandler}
                    isDisabled={appointmentsLoading}
                    complexLabel={true}
                />
                {appointmentsLoading && (
                    <div className={classes.subtitle} style={{ marginTop: "8px" }}>
                        Loading appointments...
                    </div>
                )}
                {!isAppointmentSelected && (
                    <div className={classes.subtitle} style={{ marginTop: "10px" }}>
                        Select an appointment first to enable trip details.
                    </div>
                )}

                {isAppointmentSelected && <>
                    <div className={classes.inner_title} style={{ marginTop: "10px" }}>
                        <h3>Trip Details</h3>
                    </div>

                    <div className={`${classes.location_container} ${!pickupat ? classes.location_container_reversed : ''}`}>
                        <button className={classes.reverse_button} onClick={reverseOrderHandler} type="button" title="reverse order"><i className="ti ti-arrows-sort"></i></button>
                        <div className={classes.home_address_field}>
                            {isAddressOverridden ? (
                                <div className={classes.home_address_input_row}>
                                    <InputBox
                                        label={pickupat ? 'From' : 'To'}
                                        name={'trip_home_address'}
                                        currentValue={tripHomeAddress}
                                        action={setTripHomeAddressHandler}
                                        redBorderFlag={!!tripHomeAddressError}
                                    />
                                    <button
                                        type="button"
                                        className={classes.address_action_button}
                                        onClick={revertToPatientAddress}
                                        title="Revert to patient address"
                                    >
                                        <i className="ti ti-arrow-back-up"></i>
                                    </button>
                                </div>
                            ) : (
                                <div className={classes.home_address_input_row}>
                                    <FalseInputNumber label={pickupat ? 'From' : 'To'} value={patientHomeAddress} />
                                    <button
                                        type="button"
                                        className={classes.address_action_button}
                                        onClick={startAddressOverride}
                                        title="Edit address"
                                    >
                                        <i className="ti ti-pencil"></i>
                                    </button>
                                </div>
                            )}
                            {tripHomeAddressError && (
                                <div
                                    className="m-top-4"
                                    style={{
                                        color: '#d32f2f',
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                        ...(pickupat ? { marginLeft: '10px' } : {}),
                                    }}
                                >
                                    {tripHomeAddressError}
                                </div>
                            )}
                        </div>
                        <CustomSelectGrouped label={pickupat ? 'To' : 'From'} optionsData={fullLocations} preSelectedValue={selectedoptionForClinic?.value} action={setClinicHandler} name={'location'} identifier={{type: 'location'}} />
                    </div>

                    <div className={classes.datetime_container}>
                        <DatepickerWithLabel label={'Pick-up Date'} action={setDateHandler} name={'add_new_trip_date'} defaultDate={selectedDate} minDate={new Date()}/>
                        <TimePickerWithLabel label={'Pick-up Time'} action={setTimeHandler} name={'add_new_trip_time'} defaultTime={selectedTime}/>
                    </div>

                    {isMinorPatient && <div className={classes.datetime_container}>
                        <InputBox
                            label={'Guardian Name'}
                            name={'guardian_name'}
                            currentValue={guardianName}
                            action={setGuardianNameHandler}
                        />
                        <CustomSelect
                            label={'Guardian Relationship'}
                            name={'guardian_relationship'}
                            optionsData={patientRelationshipOptionsData}
                            preSelectedValue={guardianRelationship}
                            action={setGuardianRelationshipHandler}
                            rtlDrawer={true}
                        />
                    </div>}

                    {relatedPatients && relatedPatients.length> 0 && <div className={classes.related_container}>
                        <h3>Share ride</h3>
                        <MultipleCheckboxCell items={relatedPatients} action={setRelatedPatientsArray} defaultValues={shareRide}/>
                    </div>}

                    {isDifferentHomeAddress && (
                        <div className={classes.address_reason_field}>
                            <FormTextareaBox
                                label="Reason for different address"
                                name="address_override_reason"
                                value={addressOverrideReason}
                                onChange={setAddressOverrideReasonHandler}
                                required={true}
                                helperText={`At least ${ADDRESS_OVERRIDE_REASON_MIN} characters.`}
                                errorText={addressReasonError}
                            />
                        </div>
                    )}

                    <div className={classes.form_footer}>
                        <Button type='submit' isLoading={loading} width={'full'}>
                            Fetch From Lyft
                            <i className="ti ti-chevron-right"></i>
                        </Button>
                    </div>
                </>}
            </PannelBody>
        </form>}
    
        {nextStep && <RequestRide setCanContinue={goBackHandler} data={rideData} closeModal={closeModal} onAppointmentRescheduled={resetForRescheduledAppointment} edit={edit} lyftResponse={lyftResponse} setLyftResponse={setLyftResponse}/>}

        {showConfirmation && <OutsideClickComponent task={closeConfirmationHandler}>
            <div className={classes.confirmation_modal}>
                <div className={classes.confirmation_modal_card}>
                    <div className={classes.confirmation_modal_title}>Confirmation Required</div>
                    <p className={classes.confirmation_modal_text}>{showConfirmation}</p>
                    <div className={classes.confirmation_modal_controls}>
                        <Button click={confirmHandler}>Confirm</Button> 
                        <Button btnStyle={'btn_danger'} click={closeConfirmationHandler}>Cancel</Button>
                    </div>
                </div>
            </div>    
        </OutsideClickComponent>}

        {appointmentRideConflict && pickupat && (
            <div className={classes.confirmation_modal}>
                <div className={classes.confirmation_modal_card}>
                    <div className={classes.confirmation_modal_title}>Active Ride Found</div>
                    <p className={classes.confirmation_modal_text}>
                        This appointment already has an active ride assigned.
                    </p>
                    {selectedAppointmentOption?.label && (
                        <p className={classes.confirmation_modal_text} style={{ marginTop: "8px" }}>
                            <strong>Appointment:</strong> {selectedAppointmentOption.label.replace(" | 🚗", "")}
                        </p>
                    )}
                    {showCancelRideConfirmation ? (
                        <>
                            <p className={classes.confirmation_modal_text} style={{ marginTop: "8px" }}>
                                Are you sure you want to cancel the existing ride for this appointment? This action cannot be undone.
                            </p>
                            <div className={`${classes.confirmation_modal_controls} ${classes.confirmation_modal_controls_stacked}`}>
                                <Button click={confirmCancelRideHandler} btnStyle={'btn_danger_variation'} isLoading={conflictActionLoading}>
                                    Confirm Cancel Ride
                                </Button>
                                <Button  click={() => setShowCancelRideConfirmation(false)} isLoading={conflictActionLoading}>
                                    Go Back
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className={classes.confirmation_modal_text} style={{ marginTop: "8px" }}>
                                Choose how you want to continue:
                            </p>
                            <div className={`${classes.confirmation_modal_controls} ${classes.confirmation_modal_controls_stacked}`}>

                                <Button click={createNewRideHandler} isLoading={conflictActionLoading}>
                                Create Return Trip 
                                </Button>

                                {canCancelConflictingRide && (
                                    <Button click={() => setShowCancelRideConfirmation(true)} btnStyle={'btn_danger_variation'} isLoading={conflictActionLoading}>
                                        Cancel Existing Ride + Proceed
                                    </Button>
                                )}

                                <Button  click={chooseDifferentAppointmentHandler} isLoading={conflictActionLoading}>
                                    Choose Different Appointment
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )}
    </>
}

export default AddNewTrip